import {
  Animator,
  Behaviour,
  serializable,
  LookAtConstraint,
  Camera,
  PlayableDirector,
  AssetReference,
  GameObject,
} from "@needle-tools/engine";
import { StateManager } from "./GameStateManager";
import {
  AnimatorManagerEvents,
  AnimatorStateTransitions,
  GameStateEvents,
  GameStates,
} from "./Consts";
import { Utility } from "./Utility";
import { ISubscribableEvent } from "./ISubscribableEvent";
import { EventData } from "./EventData";

///A class for handling animator state changes
export class AnimatorManager extends Behaviour implements ISubscribableEvent {
  @serializable(Animator)
  public horseAnimators?: Animator[];

  @serializable(PlayableDirector)
  public timeline?: PlayableDirector; //On the path object that controls the horses

  @serializable(Animator)
  public animatorController?: Animator;

  @serializable(AssetReference)
  container?: AssetReference[];

  @serializable(AssetReference)
  animationOptions?: AssetReference[];

  @serializable(GameObject)
  public parent?: GameObject; //The parent of downloaded models

  @serializable(LookAtConstraint)
  public lookAtConstraint?: LookAtConstraint;

  @serializable(Camera)
  public cameras?: Camera[];

  @serializable(GameObject)
  public startLights?: GameObject[]; // F1-specific start lights

  // Toggle for F1 vs Horse racing mode
  @serializable()
  public isF1: boolean = false;

  private currentObject: GameObject | null = null; //The object that gets created when we download a new model

  private minRacerAnimatorSpeed = 0.85;
  private maxRacerAnimatorSpeed = 1.15;
  private onAnimationDownloaded: (() => void)[] = [];
  private maxTimeFromAnimationEndToFastForwardTo = 10.0; //This is as close to the end of an animation we want to fast forward to. If we fast forward beyond this point, we would miss the end of the race, resulting in missing certain triggers

  private arrivalOrderArray: number[] = [];
  private static instance: AnimatorManager | null = null;
  private stateManager?: StateManager;

  private lightsEnabled: boolean = false; // F1-specific flag for lights

  // Method to create or get the singleton instance
  public static getInstance(): AnimatorManager {
    if (AnimatorManager.instance === null) {
      AnimatorManager.instance = new AnimatorManager();
    }
    return AnimatorManager.instance;
  }

  public printMessage(): void {
    console.log("AnimatorManager printMessage");
  }

  awake(): void {
    this.stateManager = StateManager.getInstance();
    //this.randomizeCycleOffsets();
  }

  onEnable() {
    // Only subscribe to warm-up state events for F1 mode
    if (this.isF1) {
      this.stateManager?.subscribeToEvent(
        GameStateEvents.ON_ENTER_WARM_UP_STATE,
        () => {
          this.handleStartLights();
        }
      );
    }
  }

  onDisable() {
    // Only unsubscribe from warm-up state events for F1 mode
    if (this.isF1) {
      this.stateManager?.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_WARM_UP_STATE,
        () => {
          this.handleStartLights();
        }
      );
    }
  }

  public startAnimation(animationName: string): void {
    // Implement the logic to start an animation
  }

  public stopAnimation(animationName: string): void {
    // Implement the logic to stop an animation
  }

  /**
   * This function is used to fast forward the animator to a specific time in the animation
   * @param timeDifference The time in seconds to fast forward by
   * @returns
   */
  public setAnimatorToSpecificTime(timeDifference: number): void {
    if (this.isF1) {
      console.log("setAnimatorToSpecificTime called");
    }
    
    const currentState = StateManager.getInstance().getCurrentState();

    if (currentState?.id !== GameStates.RACE_IN_PROGRESS_STATE) {
      return;
    }

    const currentAnimation =
      this.animatorController?.runtimeAnimatorController?.activeState?.motion;
      
    if (this.isF1) {
      console.log("Current animation", currentAnimation);
    }

    if (currentAnimation && this.animatorController) {
      const animationDuration = currentAnimation!.clip!.duration;
      
      if (this.isF1) {
        console.log("Animation duration", animationDuration);
      }

      if (timeDifference === -1) {
        if (this.isF1) {
          console.log("Negative time difference");
        }
        this.animatorController.play("DefaultState", 0, 0.0);
        this.animatorController.play(currentAnimation!.name, 0, 0);
        return;
      }

      const maxTimeToFastForwardTo =
        animationDuration - this.maxTimeFromAnimationEndToFastForwardTo;
      const currentTime = currentAnimation!.action!.time;

      if (currentTime >= maxTimeToFastForwardTo) {
        //Already within range of the finish line so dont bother fast forwarding
        return;
      }

      let newTime = currentTime + timeDifference;
      
      if (this.isF1) {
        console.log("New time", newTime);
      }

      if (newTime > maxTimeToFastForwardTo) {
        newTime = maxTimeToFastForwardTo;
      }

      const normalizedNewTime = newTime / animationDuration; // Normalize (animator state needs normalized time)
      
      if (this.isF1) {
        console.log("Normalized new time", normalizedNewTime);
      }

      this.animatorController.play("DefaultState", 0, 0.0);
      this.animatorController.play(
        currentAnimation!.name,
        0,
        normalizedNewTime,
      );

      // F1-specific additional time setting
      if (this.isF1 && this.animatorController && this.animatorController.currentAction) {
        console.log("Current action", this.animatorController.currentAction);
        this.animatorController.currentAction.time = newTime;
      }
    }
  }

  public resetHorseAnimatorStates(): void {
    if (this.horseAnimators) {
      this.horseAnimators.forEach((anim) => {
        const currentHorseAnimation =
          anim.runtimeAnimatorController?.activeState?.motion;

        if (currentHorseAnimation) {
          anim.play("InBox", 0, 0.0);
        }
      });
    }
  }

  /*
  Applies offsets to various states on the horse animators 
  */
  public randomizeCycleOffsets(): void {
    if (this.horseAnimators) {
      this.horseAnimators.forEach((anim) => {
        anim.setFloat(
          AnimatorStateTransitions.IDLE_OFFSET,
          Utility.returnRandomFloat(0, 1),
        );
        anim.setFloat(
          AnimatorStateTransitions.START_OFFSET,
          Utility.returnRandomFloat(0, 0.9),
        );
        anim.setFloat(
          AnimatorStateTransitions.START_OFFSET_SPEED_MULTIPLIER,
          Utility.returnRandomFloat(
            this.minRacerAnimatorSpeed,
            this.maxRacerAnimatorSpeed,
          ),
        );
        anim.setFloat(
          AnimatorStateTransitions.RUN_OFFSET,
          Utility.returnRandomFloat(0, 0.9),
        );
        anim.setFloat(
          AnimatorStateTransitions.SPEED_MULTIPLIER,
          Utility.returnRandomFloat(
            this.minRacerAnimatorSpeed,
            this.maxRacerAnimatorSpeed,
          ),
        );
        anim.enabled = true;
      });
    }
  }

  // F1-specific methods for handling start lights
  public returnAreLightsEnabled(): boolean {
    if (this.isF1 && this.lightsEnabled) {
      console.log("!!!!!!!!!!!!Lights are enabled");
    }
    return this.lightsEnabled;
  }

  private handleStartLights(): void {
    if (!this.isF1) return;
    
    this.lightsEnabled = true;
    if (this.startLights) {
      this.startLights.forEach((light) => {
        GameObject.setActive(light, false);
      });
    }
  }

  public startRace() {
    if (this.isF1) {
      // F1 version has no console log here
    } else {
      console.log("AnimatorManager startRace");
    }

    if (this.animatorController) {
      this.animatorController!.enabled = true;

      // Both versions have these commented out
      // this.loadAnimation(0);
      // this.animatorController.enabled = true;
      // this.timeline?.play();

      if (this.horseAnimators) {
        for (let i = 0; i < this.horseAnimators.length; i++) {
          this.horseAnimators[i].setTrigger(
            AnimatorStateTransitions.START_RACE,
          );
        }
      }
    }
  }

  public reset() {
    if (this.animatorController) {
      this.animatorController!.enabled = false;
    }
    
    if (this.isF1) {
      console.log("AnimatorManager reset called");
    }
    
    this.setAnimatorToSpecificTime(-1);
    this.resetHorseAnimatorStates();
  }

  public returnRacerAnimators(): Animator[] | undefined {
    return this.horseAnimators;
  }

  //---------- Downloading animations from the server ------------
  public load() {
    console.log("About to start to download animation");
    this.downloadAndApply();
  }

  public getArrivalOrderArray(): number[] {
    return this.arrivalOrderArray;
  }

  async downloadAndApply() {
    const eventData = EventData.getInstance();
    const racePrefix = eventData.getArrivalAsString();
    console.log("Arrival is ", racePrefix); // The first 3 racers in order for F1
    const asset = this.findAssetByPrefix(racePrefix);
    console.log("asset uri is ", asset?.uri);
  
    const instance = (await asset?.instantiate(this.parent)) as GameObject;
  
    this.currentObject?.destroy();
    this.currentObject = instance;
  
    let downloadedAnimator = instance.getComponentInChildren(Animator);
    console.log("Downloaded Animator component", downloadedAnimator);
    this.animatorController!.runtimeAnimatorController =
      downloadedAnimator!.runtimeAnimatorController;
  
    if (downloadedAnimator?.sourceId) {
      if (this.isF1) {
        // F1 version - matches F1_AnimationData_1234567890.glb format
        const match = downloadedAnimator.sourceId.match(
          /F1_AnimationData_(\d+)\.glb/,
        );
        if (match) {
          const digits = match[1];
          // Parse the digits considering "10" as a single number
          this.arrivalOrderArray = this.parseOrderString(digits);
        }
      } else {
        // Horse racing version - matches AnimationData_123456.glb format
        const match = downloadedAnimator.sourceId.match(
          /AnimationData_(\d{6})\.glb/,
        );
        if (match) {
          const digits = match[1];
          this.arrivalOrderArray = digits.split("").map(Number);
        }
      }
  
      eventData.setArrivalOrderFull(this.arrivalOrderArray);
      console.log("Arrival order array", this.arrivalOrderArray);
    }
  
    instance?.destroy();
  
    console.log("Downloaded", downloadedAnimator);
  
    this.triggerOnAnimationDownloaded();
  }
  
  // Helper method to parse order strings that might contain "10" (F1-specific)
  private parseOrderString(orderStr: string): number[] {
    if (!this.isF1) {
      // For horse racing, just convert each character to a number
      return orderStr.split("").map(Number);
    }
    
    // F1-specific parsing that handles "10" as a single number
    const result: number[] = [];
    let i = 0;
    
    while (i < orderStr.length) {
      // Check if current position could be the start of "10"
      if (i + 1 < orderStr.length && orderStr.substring(i, i + 2) === "10") {
        result.push(10);
        i += 2; // Skip both digits of "10"
      } else {
        result.push(parseInt(orderStr[i]));
        i += 1; // Move to next character
      }
    }
    
    return result;
  }
  
  private findAssetByPrefix(racePrefix: string): AssetReference | undefined {
    if (this.isF1) {
      // F1-specific asset finding logic
      // Parse the prefix string
      const prefixArray = this.parseOrderString(racePrefix);
      
      // Only care about the first position
      const winningRacer = prefixArray[0];
      console.log(`Looking for animations where racer ${winningRacer} wins`);
      
      // Collect all assets where the winning racer matches
      const matchingAssets: AssetReference[] = [];
      
      for (const asset of this.animationOptions!) {
        // Extract the digits from the F1_AnimationData_ prefix
        const match = asset.uri.match(/F1_AnimationData_(\d+)\.glb$/);
        if (match) {
          const fullOrderStr = match[1];
          const fullOrderArray = this.parseOrderString(fullOrderStr);
          
          // Check if first racer matches
          if (fullOrderArray.length > 0 && fullOrderArray[0] === winningRacer) {
            matchingAssets.push(asset);
            console.log(`Found matching asset: ${asset.uri}`);
          }
        }
      }
      
      // If we found any matching assets, select one randomly
      if (matchingAssets.length > 0) {
        const randomIndex = Math.floor(Math.random() * matchingAssets.length);
        const selectedAsset = matchingAssets[randomIndex];
        console.log(`Randomly selected asset ${randomIndex + 1} of ${matchingAssets.length}: ${selectedAsset.uri}`);
        return selectedAsset;
      }
      
      console.log(`WARNING: No animations found with racer ${winningRacer} in first position`);
      return undefined; // Return undefined if no match is found
    } else {
      // Horse racing version - simpler prefix matching
      for (const asset of this.animationOptions!) {
        // Extract the digits immediately following the underscore
        const match = asset.uri.match(/_(\d{6})\.glb$/);
        if (match && match[1].startsWith(racePrefix)) {
          return asset;
        }
      }
      return undefined; // Return undefined if no match is found
    }
  }

  private triggerOnAnimationDownloaded(): void {
    //print the onAnimationDownloaded array
    console.log("onAnimationDownloaded array", this.onAnimationDownloaded);
    this.onAnimationDownloaded?.forEach((listener) => listener());
  }

  public subscribeToEvent(eventName: string, listener: () => void): void {
    console.log("Subscribing to event in ANIMATOR MANAGER", eventName);
    if (eventName === AnimatorManagerEvents.ON_ANIMATION_DOWNLOADED) {
      if (!this.onAnimationDownloaded.includes(listener)) {
        this.onAnimationDownloaded.push(listener);
      } else {
        console.log("Listener already exists in onAnimationDownloaded array.");
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }

  public unsubscribeFromEvent(eventName: string, listener: () => void): void {
    if (eventName === AnimatorManagerEvents.ON_ANIMATION_DOWNLOADED) {
      const index = this.onAnimationDownloaded.indexOf(listener);
      if (index !== -1) {
        this.onAnimationDownloaded.splice(index, 1);
      }
    } else {
      throw new Error("Event name not recognized.");
    }
  }
}